import { useEffect } from 'react';
import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { Plus, Trash } from 'react-bootstrap-icons';
import { RETENTION_TYPE_OPTIONS, retentionTypePlaceholder, retentionTypeUnit } from 'utils/retentionPolicies';

const STATUS_OPTIONS = ['all', 'completed', 'failed', 'canceled', 'skipped', 'killed'];
function toNumberOrEmpty(v) {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  return Number.isFinite(n) ? n : '';
}

function emitPolicies(onChange, namePath, next) {
  onChange({ target: { name: namePath, value: next } });
}

const RetentionPoliciesEditor = ({ state, onChange, namePath = 'retention_policies' }) => {
  const policies = Array.isArray(state?.retention_policies) ? state.retention_policies : [];

  // Initialize the field once if it's missing, without mutating props
  useEffect(() => {
    if (!Array.isArray(state?.retention_policies)) {
      emitPolicies(onChange, namePath, []);
    }
    // eslint-disable-next-line
  }, []); // run once

  const addPolicy = () => {
    const next = [...policies, { status: 'all', retention_type: 'days_to_keep', value: 30 }];
    emitPolicies(onChange, namePath, next);
  };

  const removePolicy = (idx) => {
    const next = policies.filter((_, i) => i !== idx);
    emitPolicies(onChange, namePath, next);
  };

  const updatePolicy = (idx, field, value) => {
    const next = policies.map((p, i) => (i === idx ? { ...p, [field]: field === 'value' ? toNumberOrEmpty(value) : value } : p));
    emitPolicies(onChange, namePath, next);
  };

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0">Retention Policies</h5>
        <Button size="sm" variant="outline-primary" onClick={addPolicy}>
          <Plus className="me-1" /> Add Policy
        </Button>
      </div>

      {(policies?.length ?? 0) === 0 && (
        <div className="text-muted mb-3">
          No policies defined yet. Click <em>Add Policy</em> to create one.
        </div>
      )}

      {policies?.map((p, idx) => (
        <div key={idx} className="border rounded p-3 mb-3">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={4}>
              <Form.Group controlId={`policy-status-${idx}`}>
                <Form.Label>Status</Form.Label>
                <Form.Select value={p.status ?? 'all'} onChange={(e) => updatePolicy(idx, 'status', e.target.value)}>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group controlId={`policy-type-${idx}`}>
                <Form.Label>Retention Type</Form.Label>
                <Form.Select value={p.retention_type ?? 'days_to_keep'} onChange={(e) => updatePolicy(idx, 'retention_type', e.target.value)}>
                  {RETENTION_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} md={3}>
              <Form.Group controlId={`policy-value-${idx}`}>
                <Form.Label>Value</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={p.value === '' ? '' : (p.value ?? '')}
                    onChange={(e) => updatePolicy(idx, 'value', e.target.value)}
                    placeholder={retentionTypePlaceholder(p.retention_type)}
                  />
                  <InputGroup.Text>{retentionTypeUnit(p.retention_type)}</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Col>

            <Col xs={12} md={1} className="d-flex">
              <Button variant="outline-danger" className="ms-md-auto mt-2 mt-md-0" onClick={() => removePolicy(idx)} title="Remove policy">
                <Trash />
              </Button>
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );
};

export default RetentionPoliciesEditor;
